import type { Location } from "@larder/shared";
import { useEffect, useRef, useState } from "react";

const TYPE_ICON: Record<Location["type"], string> = {
  home: "🏠",
  office: "🏢",
  other: "📍",
};

/**
 * The header, in the quick-commerce idiom.
 *
 * Blinkit and Zepto both pin the delivery ADDRESS to the top and lead with a
 * single bold number — "10 minutes". The address is not decoration: it is the
 * one piece of context that changes everything below it, and it is tappable
 * because switching is a real, if rare, action.
 *
 * Larder's equivalent of "10 minutes" is how many things need you today. That
 * is the number that decides whether you open the app tomorrow.
 */
export function TopBar({
  location,
  all,
  onSelect,
  onAddPlace,
  onOpenSettings,
  headline,
  sub,
}: {
  location: Location;
  all: Location[];
  onSelect: (id: string) => void;
  onAddPlace: () => void;
  onOpenSettings: () => void;
  headline: string;
  sub: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  // Tapping anywhere else closes the sheet — a dropdown you can only dismiss
  // with its own control feels broken on a phone.
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  return (
    <header className="topbar">
      <div className="topbar-row">
        <div className="place-picker" ref={wrap}>
          <button
            type="button"
            className="place-button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid="place-button"
          >
            <span className="place-emoji" aria-hidden="true">
              {TYPE_ICON[location.type]}
            </span>
            <span className="place-labels">
              <span className="place-kicker">Tracking</span>
              <span className="place-name">
                <span data-testid="location-name-heading">{location.name}</span>
                <span className="chev" aria-hidden="true">
                  ▾
                </span>
              </span>
            </span>
          </button>

          {open && (
            <nav className="sheet" aria-label="switch place">
              {all.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className={`sheet-row ${l.id === location.id ? "on" : ""}`}
                  onClick={() => {
                    onSelect(l.id);
                    setOpen(false);
                  }}
                  data-testid={`place-${l.name}`}
                >
                  <span aria-hidden="true">{TYPE_ICON[l.type]}</span>
                  {l.name}
                </button>
              ))}
              <button
                type="button"
                className="sheet-row add"
                onClick={() => {
                  setOpen(false);
                  onAddPlace();
                }}
                data-testid="add-place"
              >
                <span aria-hidden="true">＋</span> Add a place
              </button>
            </nav>
          )}
        </div>

        <button
          type="button"
          className="avatar"
          onClick={onOpenSettings}
          aria-label="Settings"
          data-testid="open-settings"
        >
          ☰
        </button>
      </div>

      <div className="hero">
        <p className="hero-line" data-testid="hero-headline">
          {headline}
        </p>
        <p className="hero-sub">{sub}</p>
      </div>
    </header>
  );
}
